import React, { useEffect, useState } from 'react';
import { formatCurrency, formatNumber } from '@/lib/utils';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';
import { useSortableData } from '../hooks/useSortableData';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  BarChart3,
  Download,
  TrendingUp,
  TrendingDown,
  ShoppingCart,
  Package,
  CheckCircle,
  XCircle,
  Calendar
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ReportsPage = () => {
  const { isSeller } = useAuth();
  const { t } = useI18n();
  const [period, setPeriod] = useState('month');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  
  // Hook per ordinamento tabella top prodotti
  const { sortedData: sortedProducts, requestSort, getSortIcon, getSortClass } = useSortableData(
    report?.top_products || [], 
    { key: 'revenue', direction: 'desc' }
  );

  useEffect(() => {
    fetchReport();
  }, [period]);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/orders/report?period=${period}`);
      setReport(res.data);
    } catch (error) {
      console.error('Error fetching report:', error);
      toast.error('Errore nel caricamento del report');
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/orders/export/csv`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ordini_export_${new Date().toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Export completato');
    } catch (error) {
      toast.error('Errore nell\'export');
    } finally {
      setExporting(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-muted',
      submitted: 'bg-amber-500',
      confirmed: 'bg-blue-500',
      in_preparation: 'bg-purple-500',
      ready: 'bg-emerald-500',
      completed: 'bg-green-500',
      cancelled: 'bg-red-500'
    };
    return colors[status] || 'bg-muted';
  };

  const getStatusLabel = (status) => {
    const labels = {
      draft: 'Bozze',
      submitted: 'Inviati',
      confirmed: 'Confermati',
      in_preparation: 'In Preparazione',
      ready: 'Pronti',
      completed: 'Completati',
      cancelled: 'Annullati'
    };
    return labels[status] || status;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold">Report Ordini</h1>
          <p className="text-muted-foreground mt-1">
            Analisi e statistiche dei tuoi ordini
          </p>
        </div>
        <div className="flex gap-2">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-40" data-testid="period-select">
              <Calendar className="mr-2 h-4 w-4" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="week">Ultima Settimana</SelectItem>
              <SelectItem value="month">Ultimo Mese</SelectItem>
              <SelectItem value="year">Ultimo Anno</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={handleExportCSV} disabled={exporting} data-testid="export-csv-btn">
            <Download className="mr-2 h-4 w-4" />
            {exporting ? 'Export...' : 'Export CSV'}
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card data-testid="kpi-total-orders">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Ordini Totali
            </CardTitle>
            <ShoppingCart className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{formatNumber(report?.total_orders || 0)}</div>
            <p className="text-xs text-muted-foreground mt-1">
              nel periodo selezionato
            </p>
          </CardContent>
        </Card>

        <Card data-testid="kpi-revenue">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {isSeller ? 'Fatturato' : 'Spesa Totale'}
            </CardTitle>
            <TrendingUp className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-600">
              {formatCurrency(report?.total_revenue)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              media {formatCurrency(report?.average_order_value)}/ordine
            </p>
          </CardContent>
        </Card>

        <Card data-testid="kpi-completed">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Completati
            </CardTitle>
            <CheckCircle className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{formatNumber(report?.completed_orders || 0)}</div>
            <p className="text-xs text-green-600 mt-1">
              {report?.completion_rate || 0}% tasso completamento
            </p>
          </CardContent>
        </Card>

        <Card data-testid="kpi-cancelled">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Annullati
            </CardTitle>
            <XCircle className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-red-600">{formatNumber(report?.cancelled_orders || 0)}</div>
            <p className="text-xs text-muted-foreground mt-1">
              ordini annullati
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Status Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading">Distribuzione Stati</CardTitle>
            <CardDescription>Ordini per stato nel periodo</CardDescription>
          </CardHeader>
          <CardContent>
            {report?.status_breakdown && Object.keys(report.status_breakdown).length > 0 ? (
              <div className="space-y-4">
                {Object.entries(report.status_breakdown).map(([status, count]) => {
                  const countNum = Number(count);
                  const percentage = report.total_orders > 0 
                    ? Math.round((countNum / report.total_orders) * 100) 
                    : 0;
                  return (
                    <div key={status} className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>{getStatusLabel(status)}</span>
                        <span className="font-mono">{countNum} ({percentage}%)</span>
                      </div>
                      <div className="h-2 bg-muted rounded-full overflow-hidden">
                        <div 
                          className={`h-full ${getStatusColor(status)} transition-all duration-500`}
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <BarChart3 className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>Nessun dato disponibile</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Products (Sellers only) */}
        {isSeller && (
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Top Prodotti</CardTitle>
              <CardDescription>Prodotti più venduti per fatturato</CardDescription>
            </CardHeader>
            <CardContent>
              {sortedProducts.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead 
                        className="uppercase text-xs cursor-pointer hover:bg-muted/50 select-none"
                        onClick={() => requestSort('name')}
                      >
                        <span className="flex items-center gap-1">
                          Prodotto
                          <span className={getSortClass('name')}>{getSortIcon('name')}</span>
                        </span>
                      </TableHead>
                      <TableHead 
                        className="uppercase text-xs text-right cursor-pointer hover:bg-muted/50 select-none"
                        onClick={() => requestSort('quantity')}
                      >
                        <span className="flex items-center justify-end gap-1">
                          Qtà
                          <span className={getSortClass('quantity')}>{getSortIcon('quantity')}</span>
                        </span>
                      </TableHead>
                      <TableHead 
                        className="uppercase text-xs text-right cursor-pointer hover:bg-muted/50 select-none"
                        onClick={() => requestSort('revenue')}
                      >
                        <span className="flex items-center justify-end gap-1">
                          Fatturato
                          <span className={getSortClass('revenue')}>{getSortIcon('revenue')}</span>
                        </span>
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedProducts.slice(0, 10).map((product, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium">
                              {index + 1}
                            </span>
                            <span className="truncate max-w-48">{product.name}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {formatNumber(product.quantity)}
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {formatCurrency(product.revenue)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Package className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>Nessun prodotto venduto</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Period Info */}
        {!isSeller && (
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Periodo Analizzato</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Data Inizio</span>
                  <span className="font-medium">
                    {report?.start_date ? new Date(report.start_date).toLocaleDateString('it-IT') : '-'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Data Fine</span>
                  <span className="font-medium">
                    {report?.end_date ? new Date(report.end_date).toLocaleDateString('it-IT') : '-'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Ordini/Giorno (media)</span>
                  <span className="font-medium">
                    {period === 'week' && report?.total_orders 
                      ? formatNumber(report.total_orders / 7, 1)
                      : period === 'month' && report?.total_orders
                      ? formatNumber(report.total_orders / 30, 1)
                      : period === 'year' && report?.total_orders
                      ? formatNumber(report.total_orders / 365, 1)
                      : '0'}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ReportsPage;
